import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  TableContainer,
  Paper,
  Typography,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Chip,
  Box,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../api';
import { toast } from 'react-toastify';

const ZebraRow = styled(TableRow)(({ theme, $error }) => ({
  backgroundColor: $error ? 'rgba(255, 0, 0, 0.08)' : undefined,
  '&:nth-of-type(odd)': {
    backgroundColor: $error ? 'rgba(255, 0, 0, 0.08)' : theme.palette.action.hover
  }
}));

const normalizeKey = k => (k || '').toString().replace(/[\s_]+/g, '').toLowerCase();
const splitVals = v =>
  Array.isArray(v)
    ? v.filter(Boolean)
    : typeof v === 'string'
      ? v.split(/[;,]\s*/).map(s => s.trim()).filter(Boolean)
      : [];
const joinVals = v => {
  if (Array.isArray(v)) return v.filter(Boolean).join('; ');
  if (typeof v === 'string') return v.trim();
  return '';
};
const uniq = arr => Array.from(new Set((arr || []).filter(Boolean).map(String)));
const pickEmail = x => {
  if (!x) return '';
  if (typeof x === 'string') return x.trim();
  return (x.email || x.emailId || x.mail || x.user?.email || x.value || '').trim();
};

const menuProps = { PaperProps: { style: { maxHeight: 320 } } };

const HIDDEN_COLS = new Set([
  'trackerowner',
  'trackermanager',
  'companyname',
  'sourceurl',
  'location',
  'branch',
  'office',
  'site',
  'form',
  'proof'
]);

const NON_EDITABLE_COLS = new Set(['category', 'lawarea', 'periodicity']); // lock Periodicity like LawArea

const pad2 = n => String(n).padStart(2, '0');

const toISODateInput = v => {
  if (!v) return '';

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${pad2(v.getMonth() + 1)}-${pad2(v.getDate())}`;
  }

  const s = String(v).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
    const [m, d, y] = s.split('/');
    return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
  }

  return '';
};

export default function UploadSheetDialog({
  open,
  onClose,
  rows,
  onRowsChange,
  legalUpdateId,
  owners = [],
  managers = [],
  locations = [],
  mode = 'draft',
  submitLabel,
  onSubmit,
  rowMeta,
  onLoadMore,
  newRowTemplate
}) {
  const isCreate = mode === 'create';
  const ctaLabel = submitLabel || (isCreate ? 'Create Tasks' : 'Save Drafts');

  const [sending, setSending] = useState(false);
  const [errorRow, setErrorRow] = useState(null);
  const [applyOwner, setApplyOwner] = useState([]);
  const [applyManager, setApplyManager] = useState([]);
  const [applyLocation, setApplyLocation] = useState('');
  const [selectedRows, setSelectedRows] = useState(() => new Set());

  const headerKeys = useMemo(() => (rows[0] ? Object.keys(rows[0]) : []), [rows]);
  const visibleHeaderKeys = useMemo(() => {
    const keys = headerKeys.filter(k => !HIDDEN_COLS.has(normalizeKey(k)));
    if (keys.length <= 1) {
      return headerKeys.filter(k => !['companyname', 'sourceurl'].includes(normalizeKey(k)));
    }
    return keys;
  }, [headerKeys]);

  const ownerColKey = useMemo(
    () => headerKeys.find(k => normalizeKey(k) === 'trackerowner') || 'trackerOwner',
    [headerKeys]
  );
  const managerColKey = useMemo(
    () => headerKeys.find(k => normalizeKey(k) === 'trackermanager') || 'trackerManager',
    [headerKeys]
  );
  const locationColKey = useMemo(() => {
    const candidates = ['location', 'branch', 'office', 'site'];
    return headerKeys.find(k => candidates.includes(normalizeKey(k))) || 'location';
  }, [headerKeys]);

  // ✅ find Periodicity key, or create a default one if missing
  const periodicityColKey = useMemo(
    () => headerKeys.find(k => normalizeKey(k) === 'periodicity') || 'periodicity',
    [headerKeys]
  );

  // ✅ default Periodicity to "One-Time" if blank/missing
  useEffect(() => {
    if (!open || !rows.length) return;

    onRowsChange(prev => {
      let changed = false;
      const next = (prev || []).map(r => {
        const cur = r?.[periodicityColKey];
        const hasVal = cur != null && String(cur).trim() !== '';
        if (hasVal) return r;
        changed = true;
        return { ...r, [periodicityColKey]: 'One-Time' };
      });
      return changed ? next : prev;
    });
  }, [open, rows.length, periodicityColKey, onRowsChange]);

  const addRow = () => {
    const base = (typeof newRowTemplate === 'function' ? newRowTemplate() : {}) || {};

    const ownerVal = applyOwner.length ? joinVals(applyOwner) : (base[ownerColKey] ?? '');
    const managerVal = applyManager.length ? joinVals(applyManager) : (base[managerColKey] ?? '');
    const locationVal = applyLocation || (base[locationColKey] ?? '');

    const next = {
      ...base,
      [ownerColKey]: ownerVal,
      [managerColKey]: managerVal,
      [locationColKey]: locationVal
    };

    // ensure Periodicity exists on newly added row too
    const curP = next?.[periodicityColKey];
    if (curP == null || String(curP).trim() === '') {
      next[periodicityColKey] = 'One-Time';
    }

    setErrorRow(null);
    onRowsChange(prev => ([...(prev || []), next]));
  };

  useEffect(() => {
    if (open) setSelectedRows(new Set());
  }, [open]);

  const selectedCount = selectedRows.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  const toggleRow = idx => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAll = checked => {
    if (!checked) return setSelectedRows(new Set());
    setSelectedRows(new Set(rows.map((_, i) => i)));
  };

  const backendOwnerOptions = useMemo(() => uniq((owners || []).map(pickEmail)), [owners]);
  const backendManagerOptions = useMemo(() => uniq((managers || []).map(pickEmail)), [managers]);
  const backendLocationOptions = useMemo(
    () => uniq((locations || []).flatMap(v =>
      String(v).split(/[;,]/).map(s => s.trim()).filter(Boolean)
    )),
    [locations]
  );

  const initialOwnerSeedRef = useRef([]);
  const initialManagerSeedRef = useRef([]);
  const initialLocationSeedRef = useRef([]);

  useEffect(() => {
    if (!open) return;

    if (!initialOwnerSeedRef.current.length) {
      const s = new Set();
      (rows || []).forEach(r => splitVals(r?.[ownerColKey]).forEach(v => s.add(v)));
      initialOwnerSeedRef.current = Array.from(s);
    }
    if (!initialManagerSeedRef.current.length) {
      const s = new Set();
      (rows || []).forEach(r => splitVals(r?.[managerColKey]).forEach(v => s.add(v)));
      initialManagerSeedRef.current = Array.from(s);
    }
    if (!initialLocationSeedRef.current.length) {
      const s = new Set();
      (rows || []).forEach(r => splitVals(r?.[locationColKey]).forEach(v => s.add(v)));
      initialLocationSeedRef.current = Array.from(s);
    }
  }, [open, rows, ownerColKey, managerColKey, locationColKey]);

  const ownerMenuOptions = useMemo(
    () => uniq([...backendOwnerOptions, ...initialOwnerSeedRef.current]),
    [backendOwnerOptions]
  );
  const managerMenuOptions = useMemo(
    () => uniq([...backendManagerOptions, ...initialManagerSeedRef.current]),
    [backendManagerOptions]
  );
  const locationMenuOptions = useMemo(
    () => uniq([...backendLocationOptions, ...initialLocationSeedRef.current]),
    [backendLocationOptions]
  );

  useEffect(() => {
    if (!rows.length) return;

    const hasOwner = headerKeys.some(k => normalizeKey(k) === 'trackerowner');
    const hasManager = headerKeys.some(k => normalizeKey(k) === 'trackermanager');
    const hasLocation = headerKeys.some(k =>
      ['location', 'branch', 'office', 'site'].includes(normalizeKey(k))
    );

    if (!hasOwner || !hasManager || !hasLocation) {
      onRowsChange(prev =>
        prev.map(r => ({
          [ownerColKey]: r[ownerColKey] ?? '',
          [managerColKey]: r[managerColKey] ?? '',
          [locationColKey]: r[locationColKey] ?? '',
          ...r
        }))
      );
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleField = (rowIdx, key, val) => {
    onRowsChange(prev => prev.map((r, i) => (i === rowIdx ? { ...r, [key]: val } : r)));
  };

  const applyAll = (key, val) => {
    onRowsChange(prev =>
      prev.map((r, i) => {
        if (selectedRows.size > 0 && !selectedRows.has(i)) return r;
        return { ...r, [key]: val };
      })
    );
  };

  const handleApplyOwner = event => {
    const value = event.target.value;
    const list = typeof value === 'string' ? value.split(',') : value;
    setApplyOwner(list);
    applyAll(ownerColKey, joinVals(list));
  };

  const handleApplyManager = event => {
    const value = event.target.value;
    const list = typeof value === 'string' ? value.split(',') : value;
    setApplyManager(list);
    applyAll(managerColKey, joinVals(list));
  };

  const handleApplyLocation = event => {
    const value = event.target.value || '';
    setApplyLocation(value);
    applyAll(locationColKey, value);
  };

  const renderChips = selected => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map(v => (
        <Chip key={v} label={v} size="small" />
      ))}
    </Box>
  );

  const renderReadOnlyCell = (v) => (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        maxWidth: 260,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={v ?? ''}
    >
      {String(v ?? '').trim() || '—'}
    </Typography>
  );

  const renderCellInput = (k, v, rowIndex) => {
    const nk = normalizeKey(k);

    if (NON_EDITABLE_COLS.has(nk)) {
      return renderReadOnlyCell(v);
    }

    if (nk === 'duedate') {
      return (
        <TextField
          type="date"
          value={toISODateInput(v)}
          onChange={e => handleField(rowIndex, k, e.target.value)}
          variant="standard"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    return (
      <TextField
        value={v ?? ''}
        onChange={e => handleField(rowIndex, k, e.target.value)}
        variant="standard"
        fullWidth
      />
    );
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      // ✅ Strip Form from the payload regardless of casing/spaces
      const cleanedRows = (rows || []).map(r => {
        const out = { ...r };
        Object.keys(out).forEach(k => {
          if (normalizeKey(k) === 'form') delete out[k];
        });
        return out;
      });

      if (isCreate) {
        const missingIdx = (cleanedRows || []).findIndex(r => {
          const hasOwner = splitVals(r?.[ownerColKey]).length > 0;
          const hasManager = splitVals(r?.[managerColKey]).length > 0;
          const hasLocation = splitVals(r?.[locationColKey]).length > 0;
          const missingOwnerOrManager = !hasOwner && !hasManager;
          return missingOwnerOrManager || !hasLocation;
        });
        if (missingIdx !== -1) {
          setErrorRow(missingIdx);
          toast.error('Each row must have at least one of Owner or Manager, and Location.');
          return;
        }
      }

      if (typeof onSubmit === 'function') {
        await onSubmit(cleanedRows);
        setErrorRow(null);
        onClose();
        return;
      }

      await api.post('/draft-tracker/task-drafts', {
        legalUpdate: legalUpdateId,
        rows: cleanedRows
      });
      toast.success('Draft saved. Use “Create Task” to generate trackers.');
      setErrorRow(null);
      onClose();
    } catch (err) {
      const data = err.response?.data || {};
      const msg = data.trackerIndex
        ? `Row ${data.trackerIndex}: ${data.message}`
        : data.message || 'Save failed';
      setErrorRow(typeof data.trackerIndex === 'number' ? data.trackerIndex - 1 : null);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} flexWrap="wrap">
          <Typography variant="h6">
            {isCreate ? 'Review Tasks' : 'Preview Task Sheet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedCount > 0 ? `Editing selected: ${selectedCount}` : `Total: ${rows.length}`}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: 'background.default', p: 2.25 }}>
        {rows.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {isCreate ? 'No trackers saved' : 'No drafts saved'}
            </Typography>
            {isCreate && (
              <Button variant="contained" onClick={addRow}>
                Add new tracker
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ minWidth: 170, color: 'text.secondary' }}>
                  {selectedCount > 0 ? `Apply to selected (${selectedCount}):` : 'Apply to all:'}
                </Typography>

                <Select
                  multiple
                  displayEmpty
                  size="small"
                  value={applyOwner}
                  onChange={handleApplyOwner}
                  input={<OutlinedInput size="small" />}
                  renderValue={selected =>
                    selected.length === 0 ? (
                      <Typography color="text.secondary">Owner</Typography>
                    ) : (
                      renderChips(selected)
                    )
                  }
                  MenuProps={menuProps}
                  sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }}
                >
                  {ownerMenuOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      <Checkbox checked={applyOwner.indexOf(opt) > -1} />
                      {opt}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  multiple
                  displayEmpty
                  size="small"
                  value={applyManager}
                  onChange={handleApplyManager}
                  input={<OutlinedInput size="small" />}
                  renderValue={selected =>
                    selected.length === 0 ? (
                      <Typography color="text.secondary">Manager</Typography>
                    ) : (
                      renderChips(selected)
                    )
                  }
                  MenuProps={menuProps}
                  sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }}
                >
                  {managerMenuOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      <Checkbox checked={applyManager.indexOf(opt) > -1} />
                      {opt}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  displayEmpty
                  size="small"
                  value={applyLocation}
                  onChange={handleApplyLocation}
                  input={<OutlinedInput size="small" />}
                  renderValue={selected =>
                    !selected ? <Typography color="text.secondary">Location</Typography> : selected
                  }
                  MenuProps={menuProps}
                  sx={{ minWidth: 220, flexGrow: 1, maxWidth: 360 }}
                >
                  {locationMenuOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>

                <Box sx={{ flexGrow: 1 }} />

                <Button variant="outlined" onClick={addRow} size="small">
                  Add tracker
                </Button>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: 'background.paper'
              }}
            >
              <TableContainer sx={{ maxHeight: 460, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: 52,
                          backgroundColor: 'background.paper',
                          borderBottomColor: 'divider'
                        }}
                      >
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selectedCount > 0 && !allSelected}
                          onChange={e => toggleAll(e.target.checked)}
                        />
                      </TableCell>

                      {visibleHeaderKeys.map(h => {
                        const isNonEditable = NON_EDITABLE_COLS.has(normalizeKey(h));
                        return (
                          <TableCell
                            key={h}
                            sx={{
                              fontWeight: 800,
                              fontSize: 12,
                              letterSpacing: 0.4,
                              textTransform: 'uppercase',
                              backgroundColor: 'background.paper',
                              color: isNonEditable ? 'text.secondary' : 'text.primary',
                              borderBottomColor: 'divider'
                            }}
                          >
                            {h}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row, i) => (
                      <ZebraRow key={i} $error={i === errorRow} hover>
                        <TableCell sx={{ width: 52, borderBottomColor: 'divider' }}>
                          <Checkbox checked={selectedRows.has(i)} onChange={() => toggleRow(i)} />
                        </TableCell>

                        {visibleHeaderKeys.map(k => (
                          <TableCell key={k} sx={{ py: 1, borderBottomColor: 'divider' }}>
                            {renderCellInput(k, row[k], i)}
                          </TableCell>
                        ))}
                      </ZebraRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {rowMeta && (rowMeta.page * rowMeta.limit < rowMeta.totalRows) && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button onClick={onLoadMore}>Load more</Button>
                <Typography variant="caption" display="block" color="text.secondary">
                  Showing {Math.min(rows.length, rowMeta.totalRows)} of {rowMeta.totalRows}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.25, py: 1.5 }}>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={sending || rows.length === 0}>
          {ctaLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
